import BSideListing from './BSideListing';
import ToolAccessLabel from './ToolAccessLabel';
import OneStepAheadDetails from './OneStepAheadDetails';
import ListingVisibilityBadge from './ListingVisibilityBadge';
import { AI_DETECTOR_VISIBLE_FLAG, PLANNER_VISIBLE_FLAG, PROFILE_VISIBLE_FLAG } from '../analytics/client';
import PreviewIdeas from './PreviewIdeas';
import { MARKETPLACE, PROJECT_180 } from '../../core/preview';
import { visibleCatalog } from '../../core/catalog';
import { previewDestinations } from '../destinations';
import type { PreviewLayoutProps } from './types';
import OneStepAheadSteps from './OneStepAheadSteps';
import './editorial.css';

export default function PreviewEditorial({ updatesAction, osaVisible = false, osaAudience = 'unknown', onOsaLearnMore, programActions, marketplaceActions, marketplaceVisible = false, marketplaceAudience = 'unknown', objectivesVisible, sitesVisible, sitesAudience = 'unknown', rubricVisible = false, rubricAudience = 'unknown', writingFeedbackVisible = false, writingFeedbackAudience = 'unknown', jobsVisible = false, jobsAudience = 'unknown', jobsHref, collabVisible = false, collabAudience = 'unknown', plannerVisible = false, plannerAudience = 'unknown', profileVisible = false, profileAudience = 'unknown', aiDetectorVisible = false, aiDetectorAudience = 'unknown', ideas = {}, bSideListing = null, showVisibilityDetails = false, onToolOpen, frontDoor = null, stampCard = null, skin }: PreviewLayoutProps & { skin?: 'bench' }) {
  const catalog = visibleCatalog({ oneStepAhead: osaVisible, objectives: objectivesVisible, sites: sitesVisible, rubric: rubricVisible, marketplace: marketplaceVisible, collab: collabVisible, planner: plannerVisible, profile: profileVisible, aiDetector: aiDetectorVisible, writingFeedback: writingFeedbackVisible, jobs: jobsVisible });
  const project = catalog.find(entry => entry.id === PROJECT_180.id);
  const marketplace = catalog.find(entry => entry.id === MARKETPLACE.id);
  const companions = catalog.filter(entry => entry.size === 'companion');
  const tools = catalog.filter(entry => entry.size === 'tool');

  return (
    <main className={`preview-page preview-editorial${skin === 'bench' ? ' preview-editorial-bench' : ''}`}>
      {frontDoor ? frontDoor() : (
        <div className="preview-intro">
          <div>
            <h1>Your next chapter.<br /><em>A little more supported.</em></h1>
          </div>
          <p className="preview-intro-copy">We helped you get to the classroom. Now, we’re building for everything that comes next. Big ideas and everyday help, all from 240.</p>
        </div>
      )}

      <section className="preview-collection" aria-label="Explore the collection">
        {(companions.length > 0 || tools.length > 0) && (
          <h2 className="preview-section-label">Use today</h2>
        )}

        {companions.length > 0 && (
          <section className="preview-companions" aria-labelledby="preview-companions-heading">
            <div className="preview-section-heading"><h2 id="preview-companions-heading">Help for the whole unit.</h2><p>Stays with you while you learn what you’re teaching next.</p></div>
            {companions.map(companion => (
              <article className="preview-featured preview-featured-plain preview-companion" key={companion.id} aria-labelledby={`${companion.id}-heading`}>
                <div className="preview-featured-content">
                  <div className="preview-feature-meta"><span className="preview-badge">Available</span></div>
                  <h3 id={`${companion.id}-heading`}>{companion.name}</h3>
                  {companion.destination && <ToolAccessLabel destination={companion.destination} />}
                  {companion.id === 'one_step_ahead' && showVisibilityDetails && <ListingVisibilityBadge audience={osaAudience} flagId={900567} />}
                  <p className="preview-summary">{companion.description}</p>
                  {companion.id === 'one_step_ahead' && <OneStepAheadSteps />}
                  {companion.destination && <div className="preview-actions">
                    <a className="preview-notify" href={previewDestinations(window.location.hostname)[companion.destination]}
                      onClick={() => onToolOpen(companion.id)}>Open {companion.name}</a>
                  </div>}
                  {companion.id === 'one_step_ahead' && <OneStepAheadDetails onOpen={onOsaLearnMore} />}
                </div>
              </article>
            ))}
          </section>
        )}

        {tools.length > 0 && (
          <section className="preview-tools" aria-labelledby="preview-tools-heading">
            <div className="preview-section-heading"><h2 id="preview-tools-heading">Small tools. Meaningful help.</h2><p>One useful thing for the task in front of you.</p></div>
            <div className="preview-tool-grid">
              {tools.map(tool => (
                <article className="preview-tool" key={tool.id} aria-labelledby={`${tool.id}-heading`}>
                  <span className="preview-tool-icon" aria-hidden="true">{tool.id === 'objectives' ? '◎' : '▤'}</span>
                  <div>
                    <span className="preview-badge">{tool.id === 'jobs' && !jobsHref ? 'In development' : 'Available'}</span>
                    <h3 id={`${tool.id}-heading`}>{tool.name}</h3>
                    {tool.destination && (tool.id !== 'jobs' || jobsHref) && <ToolAccessLabel destination={tool.destination} />}
                    {tool.id === 'jobs' && showVisibilityDetails && <ListingVisibilityBadge audience={jobsAudience} flagId={911638} />}
                    {tool.id === 'sites' && showVisibilityDetails && <ListingVisibilityBadge audience={sitesAudience} flagId={888906} />}
                    {tool.id === 'rubric' && showVisibilityDetails && <ListingVisibilityBadge audience={rubricAudience} flagId={894436} />}
                    {tool.id === 'writing_feedback' && showVisibilityDetails && <ListingVisibilityBadge audience={writingFeedbackAudience} flagKey="preview-writing-feedback-visible" />}
                    {tool.id === 'collab' && showVisibilityDetails && <ListingVisibilityBadge audience={collabAudience} flagId={905683} />}
                    {tool.id === 'planner' && showVisibilityDetails && <ListingVisibilityBadge audience={plannerAudience} flagKey={PLANNER_VISIBLE_FLAG} />}
                    {tool.id === 'profile' && showVisibilityDetails && <ListingVisibilityBadge audience={profileAudience} flagKey={PROFILE_VISIBLE_FLAG} />}
                    {tool.id === 'ai_detector' && showVisibilityDetails && <ListingVisibilityBadge audience={aiDetectorAudience} flagKey={AI_DETECTOR_VISIBLE_FLAG} />}
                    <p>{tool.description}</p>
                    {tool.destination && (tool.id !== 'jobs' || jobsHref) && <a className="preview-tool-link" href={tool.id === 'jobs' ? jobsHref : previewDestinations(window.location.hostname)[tool.destination]}
                      onClick={() => onToolOpen(tool.id)}>Open {tool.name} <span aria-hidden="true">↗</span></a>}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <h2 className="preview-section-label">What we’re exploring</h2>

        {(project || marketplace) && (
          <section aria-label="Upcoming products">
            {project && (
            <article className="preview-featured" aria-labelledby="project-180-heading">
              <div className="preview-featured-content">
                <div className="preview-feature-meta"><span className="preview-eyebrow">Made for your first year</span><span className="preview-badge">Coming soon</span></div>
                <h3 id="project-180-heading">{PROJECT_180.name}</h3>
                <p className="preview-summary">{project.description}</p>
                <div className="preview-actions">
                  {programActions}
                </div>
                <details className="preview-program-details">
                  <summary>Inside Project 180</summary>
                  <p className="preview-lede">{PROJECT_180.lede}</p>
                  <p className="preview-beats-heading">{PROJECT_180.beatsHeading}</p>
                  <ul className="preview-beats">
                    {PROJECT_180.beats.map((beat) => (
                      <li key={beat.title}>
                        <span className="preview-beat-title">{beat.title}</span>
                        <span>{beat.body}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
              <div className="preview-art" aria-hidden="true">
                <div className="preview-book"><span>A teacher’s field guide</span><strong>Project<br />180.</strong><p>Your first 180 days.<br />A stronger start.</p><span>240 · Preview</span></div>
                <span className="preview-art-note">A little wiser, every day.</span>
              </div>
            </article>
            )}
            {marketplace && (
            <article className="preview-featured preview-featured-market" aria-labelledby="marketplace-heading">
              <div className="preview-art preview-market-art" aria-hidden="true">
                <span className="preview-art-note">Ready to teach Monday.</span>
                <div className="preview-market-covers">
                  {MARKETPLACE.covers.map((cover) => (
                    <div className="preview-market-cover" key={cover.title}><span>{cover.level}</span><strong>{cover.title}</strong><small>{cover.detail}</small></div>
                  ))}
                </div>
                <div className="preview-market-shelf" />
              </div>
              <div className="preview-featured-content">
                <div className="preview-feature-meta"><span className="preview-eyebrow">{MARKETPLACE.tagline}</span><span className="preview-badge">Planned</span></div>
                <h3 id="marketplace-heading">{MARKETPLACE.name}</h3>
                {showVisibilityDetails && <ListingVisibilityBadge audience={marketplaceAudience} flagId={896665} />}
                <p className="preview-summary">{marketplace.description}</p>
                <div className="preview-actions">
                  {marketplaceActions}
                </div>
                <details className="preview-program-details">
                  <summary>Inside {MARKETPLACE.name}</summary>
                  <p className="preview-lede">{MARKETPLACE.lede}</p>
                  <p className="preview-beats-heading">{MARKETPLACE.beatsHeading}</p>
                  <ul className="preview-beats">
                    {MARKETPLACE.beats.map((beat) => (
                      <li key={beat.title}>
                        <span className="preview-beat-title">{beat.title}</span>
                        <span>{beat.body}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            </article>
            )}
          </section>
        )}

        <BSideListing listing={bSideListing} onOpen={() => onToolOpen('b_side')} />
        <PreviewIdeas ideas={ideas} showVisibilityDetails={showVisibilityDetails} />

        {updatesAction}
        {stampCard}
      </section>

      <footer className="preview-footnote"><strong>240 / Built around teachers.</strong><p>
        240 Labs features may change or disappear. Sign in to your 240 account to get notified and save your choices across devices.
      </p></footer>

    </main>
  );
}
